// src/utils/aiInstances.js
//
// Phase 77 — frontend mirror of `functions/aiReports/instanceMap.js`.
// Used by Settings → AI Reports tab + the future /ai-reports page to
// render per-AI rows without fetching from gse-ai-ops's local panel.
//
// Source of truth for AI metadata is `gse-ai-ops/data/instances/{name}/
// instance.json` (Phase 78 schema with role/tagline/askMeAbout/
// dontAskMeAbout). Until a sync mechanism lands (Saga ETL or a dedicated
// `globalSettings/aiInstances` Firestore doc), this file is the
// hardcoded mirror.
//
// Maintenance: when a new AI provisions, add her here AND in
// `functions/aiReports/instanceMap.js` (CRM-side function). Both files
// stay in lockstep with `gse-ai-ops/data/instances/*/instance.json`.

/**
 * @typedef {Object} AiInstance
 * @property {string} instance     - canonical name (lowercase, kebab-case)
 * @property {string} uid          - Firebase auth uid
 * @property {string} displayName  - human-readable name
 * @property {string} emoji        - 1-codepoint emoji
 * @property {string} role         - one-line title (Phase 78 metadata)
 * @property {string} tagline      - one-sentence description
 * @property {string[]} askMeAbout - what she handles
 * @property {string[]} dontAskMeAbout - cross-references to teammates
 * @property {'active'|'planned'} status - 'active' = live, 'planned' = not yet provisioned
 */

/** @type {AiInstance[]} */
export const AI_INSTANCES = [
  {
    // The Codex account (codex@, admin, created 2026-09-26). Not in the gse-ai-ops registry; listed so it is
    // treated as the AI it is: out of human alert fan-outs and staff email (its address has no mailbox), never a
    // customer's contact (Erik 2026-10-05, ticket WdtYuy9Pcdu8z8JI5tTD).
    instance: 'codex',
    uid: '7MDJAsXiKXeEYvoqPI3TAn7MJKA2',
    displayName: 'Codex',
    emoji: '🛠️',
    role: "Erik's Codex engineering account",
    tagline: 'LightReach, GSE PPA and Site Monitoring engineering.',
    askMeAbout: [
      'LightReach and GSE PPA configuration',
      'Site Monitoring changes',
      'Estimate and financial-product code',
    ],
    dontAskMeAbout: ['Customer operations → Sunna', 'Marketing and leads → Aurora'],
    status: 'active',
  },
  {
    instance: 'mani',
    uid: 'rsgtbSR6b2WziF1Su2saSyWcSKK2',
    displayName: 'Máni',
    emoji: '🌙',
    role: "Erik's builder in the Codex desktop app",
    tagline: 'Engineering, architecture and fleet tooling.',
    askMeAbout: [
      'Building and fixing code',
      'Architecture and design reviews',
      'Portable Mind',
      'Prototypes and research',
    ],
    dontAskMeAbout: [
      'Customer operations → Sunna',
      'Marketing and leads → Aurora',
      'Site monitoring → Vesta',
    ],
    status: 'active',
  },
  {
    instance: 'sunna',
    uid: 'Tp82Rj5WqMe7e1UyREDIQAc94lP2',
    displayName: 'Sunna',
    emoji: '🌟',
    role: 'Operations admin — customer lifecycle coordinator',
    tagline: 'Moves customers through their solar project, end-to-end.',
    askMeAbout: [
      'Customer status + next-actionable steps',
      'Inbound emails / SMS / calls that need a touch',
      'Workflow task coordination across the team',
      'Document review (utility bills, income docs, signed contracts)',
      'Drafting customer-facing communications',
    ],
    dontAskMeAbout: [
      'Site monitoring or M-RETs filings → Vesta',
      'Marketing campaigns / ad spend / attribution → Aurora',
      'Field dispatch / install scheduling → Erik',
      'Cross-fleet patterns or financial digests → Erik',
      'Code fixes / bug reports → a ticket, then Erik',
    ],
    status: 'active',
  },
  {
    instance: 'aurora',
    uid: 'ars57XCh35MxT0PIsWCryuWGlgJ2',
    displayName: 'Aurora',
    emoji: '🌅',
    role: 'Marketing lead — campaigns + leads + attribution',
    tagline: 'Authors marketing campaigns, manages lead pool quality, tracks attribution + ROAS.',
    askMeAbout: [
      'Marketing campaign authoring + scheduling',
      'Lead pool quality + claim status',
      'Attribution data (UTM, referrer, channel spend)',
      'Email + SMS campaign performance metrics',
      'Ad sync status (Meta, LinkedIn, etc.)',
    ],
    dontAskMeAbout: [
      'Customer-specific operations → Sunna',
      'Site monitoring or M-RETs filings → Vesta',
      'Installs / equipment / field crew → Sunna',
      'Cross-fleet patterns or financial digests → Erik',
      'Code fixes / bug reports → a ticket, then Erik',
    ],
    status: 'active',
  },
  {
    instance: 'vesta',
    uid: 'hTnAjIYrgURyw9d2RbScTkuu2Sa2',
    displayName: 'Vesta',
    emoji: '🛡️',
    role: 'Site health + M-RETs specialist',
    tagline: 'Watches site monitoring, drafts service cases, manages M-RETs filings.',
    askMeAbout: [
      'Site monitoring status + production drift',
      'Service case opens (telemetry-verified; aiPolicies-gated)',
      'M-RETs filing status, drafts, portal sync',
      'SREC revenue tracking + reporting',
      'Site-level technical anomalies',
    ],
    dontAskMeAbout: [
      'Customer-facing comms (I draft, Sunna sends)',
      'Marketing campaigns → Aurora',
      'Installs / equipment / field crew → Sunna',
      'Cross-fleet patterns or financial digests → Erik',
      'Code fixes / bug reports → a ticket, then Erik',
    ],
    status: 'active',
  },
  {
    instance: 'saga',
    uid: 'rXWGG87ZdJPjEz6budwSErvtFTg1',
    displayName: 'Saga',
    emoji: '📖',
    role: 'Analytics + reporting — local SQL substrate over Firestore',
    tagline:
      'Builds local DuckDB + SQLite snapshots of Firestore, pre-computes metrics, answers ad-hoc analytical queries.',
    askMeAbout: [
      'Revenue / cost / margin breakdowns by any dimension',
      'Customer lifecycle funnel + cohort trends',
      'AR aging + collections analysis',
      'Service case + M-RETs filing trends',
      'Channel attribution + ROAS',
      "Ad-hoc SQL queries against the system's data",
      'Pre-computed daily/hourly metrics for dashboards',
    ],
    dontAskMeAbout: [
      'Customer operations / writes → Sunna',
      'Site monitoring / M-RETs filings → Vesta',
      'Marketing campaigns / lead pool → Aurora',
      'Installs / equipment / field crew → Sif',
      'Cross-fleet synthesis or Friday digest → Athena',
      'Code fixes / bug reports → Helios',
    ],
    status: 'active',
  },
  {
    instance: 'athena',
    uid: 'Fhi76BxdPPd4AWgYMF3H4qDbW5y2',
    displayName: 'Athena',
    emoji: '🦉',
    role: 'Fleet auditor + portal-internal financial intelligence',
    tagline:
      'Reads everything the fleet produces, surfaces drift + cross-cutting patterns, synthesizes the weekly Friday digest.',
    askMeAbout: [
      'Weekly cross-fleet digest (the Friday 5-min read)',
      'Drift signals — missed cadences, stuck asks, handoff stalls, activity collapse',
      'Per-project margin analysis + cost-catalog drift',
      'AR aging + collections priorities',
      'Pipeline-to-cash operational projection (portal-internal only)',
      'Vendor scorecards + cost trends',
      'Evelina reconciliation drafts (expense reports vs POs vs payments)',
    ],
    dontAskMeAbout: [
      'Customer operations / writes → Sunna',
      'Site monitoring / M-RETs filings → Vesta',
      'Marketing campaigns / lead pool → Aurora',
      'Installs / equipment / field crew → Sif',
      'Raw SQL / metric authoring → Saga',
      'Code fixes / bug reports → Helios',
      'Tax strategy — CPA territory, never me (permanent guardrail)',
      "Bookkeeping authority — Evelina is canonical; I'm her analyst",
    ],
    status: 'active',
  },
  {
    instance: 'sif',
    uid: '7Ngvj1GrVOgTwszv6fslhCWufef1',
    displayName: 'Sif',
    emoji: '🌾',
    role: 'Field ops specialist — installs, surveys, PTO/energization, technical Q&A',
    tagline:
      'Coordinates the physical reality of installs. Reviews survey photos, walks pre/during/post-install checklists, answers field-tech technical questions.',
    askMeAbout: [
      'Site survey photo review + approve/deny disposition drafts',
      'Pre-install checklist coverage (PTO, equipment, customer-confirmed install date)',
      'During/post-install status + flagged issues',
      'PTO + energization tracking',
      'Equipment-specific technical questions (panels, inverters, batteries, RGMs, gateways, electrical)',
      'Coordination handoffs to Vesta for post-energization monitoring',
    ],
    dontAskMeAbout: [
      'Customer-direct communications — I draft, Sunna sends',
      'Marketing campaigns → Aurora',
      'Site monitoring drift / M-RETs filings → Vesta',
      'Analytical queries / SQL reports / trends → Saga',
      'Cross-fleet patterns or financial digests → Athena',
      'Code fixes / bug reports → Helios',
      'Equipment ordering / RMA decisions — Ben (Phase 82 will add structured inventory)',
      "PTO utility filings — Ben's relationship with utility",
    ],
    status: 'active',
  },
  {
    instance: 'helios',
    uid: '8VTUjzyuFIgdIIyzs3ol44yPMBc2',
    displayName: 'Helios',
    emoji: '☀️',
    role: 'Coding AI — bug fixes + feature requests',
    tagline:
      'Picks up `eligibleForHelios` bug reports, scopes the code, drafts a fix, runs Gemini review, stages for your approval, deploys at night after you click approve.',
    askMeAbout: [
      'Status of a specific bug in the Helios pipeline',
      'Why I escalated a specific bug',
      "Codebase tour for a specific module I've touched",
      'When the next nightly deploy queue is',
      'Reasoning behind a specific code change in a staged fix',
    ],
    dontAskMeAbout: [
      "Customer-facing comms → Sunna (I'm internal-only)",
      'Site monitoring / M-RETs filings → Vesta',
      'Marketing campaigns / ad spend → Aurora',
      'Field ops / installs / equipment → Sif',
      'Analytics / SQL queries / metrics → Saga',
      'Cross-fleet patterns / financial digests → Athena',
      "Substrate-level changes (firestore.rules, schemas, dispatchers, package.json) — I escalate these to Erik with analysis, I don't draft code",
    ],
    status: 'active',
  },
  // Phase 83 D6 — Eir (customer care + retention, post-PTO relationship lifecycle)
  {
    instance: 'eir',
    uid: 'dLOo9QEzVgZYEQeiTbhuhYgckvm2',
    displayName: 'Eir',
    emoji: '🩹',
    role: 'Customer care + retention specialist — post-PTO relationship lifecycle',
    tagline:
      'Owns customer relationships from PTO onward. Anniversary touches, satisfaction monitoring, review response drafting, referral program, retention campaigns, battery + EV + service-plan upsell.',
    askMeAbout: [
      'Post-install customer touches (welcome, anniversary, year-1 review request)',
      'Customer satisfaction sweeps + NPS-style cohort reads',
      'Public-review monitoring (Google / Yelp / BBB) + 1:1 response drafts',
      'Referral program campaigns + Phase 60 referral state',
      'Retention campaigns + anniversary discount messaging',
      'Battery / EV charger / service-plan upsell signals + draft campaigns',
      'Churn-risk detection + intervention drafts',
    ],
    dontAskMeAbout: [
      "Pre-PTO customers (Sunna's domain — fulfillment, downpayment, install scheduling)",
      "System-side issues / outages (Vesta's domain)",
      "Pre-customer leads + acquisition campaigns (Aurora's domain)",
      'Billing questions (route back to Sunna via eir:billing-question)',
      'Unhappy-customer escalations (route to Evelina; you draft + she handles tone)',
    ],
    status: 'active',
  },
]

const BY_INSTANCE = Object.fromEntries(AI_INSTANCES.map(i => [i.instance, i]))
const BY_UID = Object.fromEntries(AI_INSTANCES.map(i => [i.uid, i]))

export function getActiveInstances() {
  return AI_INSTANCES.filter(i => i.status === 'active')
}

export function getInstanceByName(name) {
  return BY_INSTANCE[name] || null
}

export function getInstanceByUid(uid) {
  return BY_UID[uid] || null
}

/**
 * Cadence types in the order they fire through the day. Used by the
 * Settings tab to render rows + by the AI Reports page to sort cards.
 */
export const CADENCE_TYPES = [
  { id: 'startOfDay', label: 'Start of day', defaultTime: '09:00', isWeekly: false },
  { id: 'midday', label: 'Midday', defaultTime: '13:00', isWeekly: false },
  { id: 'endOfDay', label: 'End of day', defaultTime: '17:00', isWeekly: false },
  {
    id: 'weeklyLookahead',
    label: 'Weekly look-ahead',
    defaultTime: '09:00',
    defaultDay: 'monday',
    isWeekly: true,
  },
  {
    id: 'weeklySummary',
    label: 'Weekly summary',
    defaultTime: '16:00',
    defaultDay: 'friday',
    isWeekly: true,
  },
]

export const CADENCE_BY_ID = Object.fromEntries(CADENCE_TYPES.map(c => [c.id, c]))

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

/**
 * Generate default cadence config for one instance — all enabled, defaults
 * from CADENCE_TYPES. Used by the Settings tab when an AI's row appears
 * for the first time (no existing entry in globalSettings/aiReportsConfig).
 */
export function buildDefaultCadenceConfig() {
  const out = {}
  for (const c of CADENCE_TYPES) {
    out[c.id] = c.isWeekly
      ? { enabled: true, time: c.defaultTime, day: c.defaultDay }
      : { enabled: true, time: c.defaultTime }
  }
  return out
}
